import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import SearchBox from "./components/SearchBox.js";
import CategoryFilter from "./components/CategoryFilter.js";
import PostGrid from "./components/PostGrid.js";
import blogApi from "services/blogApi.js";
import "assets/css/blog-content.css";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: "70px",
    paddingBottom: "50px",
    maxWidth: "1140px",
    margin: "0 auto",
    padding: "0 20px",
  },
  title: {
    fontSize: "3rem",
    fontWeight: "700",
    marginBottom: "50px",
    textAlign: "center",
    color: "#3C4858",
    fontFamily: "'Adelle-sans', sans-serif",
  },
  subheader: {
    fontSize: "1rem",
    fontWeight: "400",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: "2rem",
    fontWeight: "400",
    marginBottom: "30px",
    color: "#3C4858",
  },
  section: {
    marginBottom: "80px",
  },
  filterSection: {
    marginBottom: "30px",
    display: "flex",
    alignItems: "flex-end",
    gap: "20px",
    flexWrap: "wrap",
  },
  loadingText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#999",
    marginTop: "50px",
  },
  errorText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#f44336",
    marginTop: "50px",
  },
  noPostsText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#999",
    marginTop: "50px",
  },
}));

export default function BlogList() {
  const classes = useStyles();
  const searchBoxRef = useRef(null);

  // SEO: Set document title for blog list page
  useEffect(() => {
    document.title = "WeVett Realty Blog - Real Estate News, Tips & Insights";

    // Update meta description for blog list
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Discover the latest real estate news, tips, and insights from WeVett Realty. Stay informed about market trends, buying guides, and expert advice."
      );
    } else {
      const newMetaDescription = document.createElement("meta");
      newMetaDescription.name = "description";
      newMetaDescription.content =
        "Discover the latest real estate news, tips, and insights from WeVett Realty. Stay informed about market trends, buying guides, and expert advice.";
      document.head.appendChild(newMetaDescription);
    }
  }, []);
  const [latestPosts, setLatestPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [postsResponse, categoriesResponse] = await Promise.all([
          blogApi.getRecentPosts(3),
          blogApi.getPopularCategories(6),
        ]);

        if (postsResponse.success) {
          setLatestPosts(postsResponse.data);
        }

        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data);
        }

        // Load all posts for initial filter view
        const allPostsResponse = await blogApi.getAllPosts();
        if (allPostsResponse.success) {
          setFilteredPosts(allPostsResponse.data);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const updateCategories = useCallback(async (searchQuery) => {
    try {
      const categoriesResponse = await blogApi.getPopularCategories(
        6,
        searchQuery
      );
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }
    } catch (err) {
      console.error("Error updating categories:", err);
    }
  }, []);

  const performSearch = useCallback(
    async (query, categoryId) => {
      try {
        setFilterLoading(true);

        // Check if search box is focused before the search
        const wasSearchFocused =
          searchBoxRef.current && searchBoxRef.current.isFocused();

        // Update categories with search-filtered counts
        await updateCategories(query);

        let response;
        if (query && query.trim() !== "") {
          // Search posts (with optional category filter)
          response = await blogApi.searchPosts(query.trim(), categoryId);
        } else if (categoryId && categoryId !== "") {
          // Filter by category only
          response = await blogApi.getPostsByCategory(categoryId);
        } else {
          // Show all posts
          response = await blogApi.getAllPosts();
        }

        if (response.success) {
          setFilteredPosts(response.data);
        }

        // Restore focus if search box was focused
        if (wasSearchFocused && searchBoxRef.current) {
          setTimeout(() => {
            searchBoxRef.current.focus();
          }, 0);
        }
      } catch (err) {
        console.error("Error searching/filtering posts:", err);
      } finally {
        setFilterLoading(false);
      }
    },
    [updateCategories]
  );

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
    // Perform search/filter with current search query and new category
    performSearch(searchQuery, categoryId);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    // Don't perform search immediately - let useEffect handle it with debounce
  };

  // Debounce search functionality
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery, selectedCategory);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingText}>Loading blog posts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <div className={classes.errorText}>Error loading posts: {error}</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h1 className={classes.title}>WeVett Realty Blog</h1>
      <div className={classes.subheader}>
        <p>
          Explore the world of military homeownership with us! Whether you’re
          looking to sell or purchase a home in preparation for your next PCS,
          or trying to learn more about military homeownership, our blog is your
          go-to resource.
        </p>
      </div>
      {/* Latest Posts Section */}
      <div className={classes.section}>
        <h2 className={classes.sectionTitle}>Latest Posts</h2>
        {latestPosts.length === 0 ? (
          <div className={classes.noPostsText}>No recent posts found.</div>
        ) : (
          <PostGrid posts={latestPosts} formatDate={formatDate} />
        )}
      </div>

      {/* Filter Section */}
      <div className={classes.section}>
        <h2 className={classes.sectionTitle}>All Posts</h2>

        <div className={classes.filterSection}>
          <SearchBox
            ref={searchBoxRef}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            disabled={filterLoading}
          />

          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryFilter}
            disabled={filterLoading}
          />

          {filterLoading && <span>Loading...</span>}
        </div>

        {filteredPosts.length === 0 ? (
          <div className={classes.noPostsText}>
            {searchQuery
              ? `No posts found for "${searchQuery}".`
              : "No posts found for the selected filter."}
          </div>
        ) : (
          <PostGrid posts={filteredPosts} formatDate={formatDate} />
        )}
      </div>
    </div>
  );
}
